import { useEffect, useRef } from 'react';

import { cn } from '@/lib/utils';

import { Professional, Practice } from '@aldebaran/prisma';
import { getVisibleWelcomeMessage } from '@aldebaran/common/util/welcomeMessage';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselDots,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { LandingPageFooter } from '@/pages/onboarding/LandingPageFooter';
import { PatientPageLayout } from '@/components/PatientPageLayout';
import { SelectableProfessional } from '@/pages/onboarding/SelectableProfessional';

interface ProfessionalSelectProps {
  mode: string | undefined;
  professional: Professional | undefined;
  professionals: Professional[];
  selectedProfessional: Professional | null;
  practice: Practice | undefined;
  onSelectProfessional: (professional: Professional | null) => void;
  onPrepareConsultation: () => void;
}

export const ProfessionalSelect = ({
  mode,
  professional,
  professionals,
  selectedProfessional,
  practice,
  onSelectProfessional,
  onPrepareConsultation,
}: ProfessionalSelectProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const welcomeMessage = getVisibleWelcomeMessage(professional);
  const isPracticeMode = practice?.onboardingDisplayMode === 'PRACTICE';
  const showNav =
    !isPracticeMode && professionals.length >= 3 && !selectedProfessional;
  const items = isPracticeMode
    ? ([professionals[0] ?? professional].filter(Boolean) as Professional[])
    : professional
    ? [professional]
    : professionals;

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current
        .querySelector<HTMLElement>('[data-slot="carousel"]')
        ?.focus();
    }
  }, []);

  return (
    <PatientPageLayout
      title={
        <h1 className="text-2xl min-[23rem]:text-3.5xl font-semibold leading-tight text-indigo-900 text-center">
          Préparez votre <br /> consultation avant <br /> votre rendez-vous
        </h1>
      }
      titleClassName="text-center"
      contentClassName="flex flex-col items-center justify-start text-center"
      footerClassName={!professional && !isPracticeMode ? '!border-0' : ''}
      footer={
        professional || isPracticeMode ? (
          <div className="w-full flex flex-col items-center gap-4">
            <LandingPageFooter
              buttonLabel="Préparer la consultation"
              onClick={onPrepareConsultation}
            />
          </div>
        ) : (
          <div />
        )
      }
    >
      <div
        ref={containerRef}
        className={`w-full flex flex-col text-center gap-2 sm:mt-0`}
      >
        <Carousel
          opts={{ align: 'center', containScroll: 'trimSnaps' }}
          className="w-full mx-auto"
        >
          <CarouselContent className="w-full items-start ml-0 ">
            <CarouselItem
              className="basis-[calc(50cqw-7.25rem)] shrink-0 "
              aria-hidden
            />
            {items.map((p, index) => (
              <CarouselItem
                key={`${p.id}-${index}`}
                className=" basis-54 shrink-0"
              >
                <SelectableProfessional
                  selectable={!isPracticeMode && items.length > 1}
                  professional={p}
                  practice={practice}
                  onSelect={
                    isPracticeMode
                      ? () => {
                          /* no-op in practice mode */
                        }
                      : mode === 'SALLE_ATTENTE' || professionals.length > 1
                      ? () => {
                          onSelectProfessional(professional ? null : p);
                        }
                      : () => {
                          /* no-op when single professional */
                        }
                  }
                  onClose={
                    !isPracticeMode &&
                    (mode === 'SALLE_ATTENTE' || mode === 'PRACTICE_LINK') &&
                    professionals.length > 1
                      ? () => onSelectProfessional(null)
                      : undefined
                  }
                  selected={
                    !isPracticeMode && selectedProfessional?.id === p.id
                  }
                  close={
                    !isPracticeMode &&
                    (mode === 'SALLE_ATTENTE' || mode === 'PRACTICE_LINK') &&
                    professionals.length > 1
                  }
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          {showNav && (
            <>
              <CarouselPrevious />
              <CarouselNext />
            </>
          )}
          {!isPracticeMode && items.length >= 4 && !selectedProfessional && (
            <CarouselDots count={items.length} scrollSnapOffset={0} />
          )}
        </Carousel>
        <div
          className={cn(
            'flex flex-col justify-center items-center gap-y-2 w-full',
          )}
        >
          {!isPracticeMode && !selectedProfessional && (
            <p className="text-indigo-900 text-center text-lg n2xs:text-xl mt-10">
              Sélectionnez le médecin avec <br /> qui vous avez rendez-vous.
            </p>
          )}

          {welcomeMessage && (
            <p className="n2xs:w-82.5 max-w-full rounded-3xl bg-white/65 px-6 py-4">
              {welcomeMessage}
            </p>
          )}
        </div>
      </div>
    </PatientPageLayout>
  );
};
